import React, { useMemo, useState } from "react";

import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Modal, Radio } from "antd";

import { PICKUP_TYPE_KEYS } from "../../SupplyPickup/SupplyPickupTabGroup/PickupRequests/utils";

import { ReactComponent as DeliveryIcon } from "../../../assets/icons/Common/Delivery.svg";
import { ReactComponent as PackageIcon } from "../../../assets/icons/Common/Package.svg";
import { ReactComponent as ClockIcon } from "../../../assets/icons/Supply & Pickup/clock.svg";

import "./ScheduleShipmentModal.css";

const LOGISTICS = {
  COURIER: "Courier",
  FEDEX: "Fedex",
};

const PICKUP_METHODS = [
  {
    key: PICKUP_TYPE_KEYS.FEDEX_PICKUP,
    title: "FedEx Pickup",
    description: "We'll arrange a FedEx driver to collect your boxes directly from your facility.",
    Icon: PackageIcon,
  },
  {
    key: PICKUP_TYPE_KEYS.LOCAL_COURIER,
    title: "Local Courier",
    description: (
      <>
        Our local courier partners will pick up your boxes. Orders must be placed before
        {" "}
        <strong>7AM for same-day pickup.</strong>
      </>
    ),
    Icon: ClockIcon,
  },
  {
    key: PICKUP_TYPE_KEYS.FEDEX_DROPOFF,
    title: "FedEx Drop-Off",
    description: "Print your label and drop off your boxes at any nearby FedEx location at your convenience.",
    Icon: DeliveryIcon,
  },
];

const STEPS = {
  CHOICE: 0,
  PICKUP_METHOD: 1,
};

const getAllowedPickupKeys = (logistics) => {
  const normalized = typeof logistics === "string" ? logistics.trim().toLowerCase() : "";
  if (normalized === LOGISTICS.COURIER.toLowerCase()) {
    return new Set(PICKUP_METHODS.map((method) => method.key));
  }
  if (normalized === LOGISTICS.FEDEX.toLowerCase()) {
    return new Set([
      PICKUP_TYPE_KEYS.FEDEX_PICKUP,
      PICKUP_TYPE_KEYS.FEDEX_DROPOFF,
    ]);
  }
  return new Set([PICKUP_TYPE_KEYS.FEDEX_DROPOFF]);
};

export default function ScheduleShipmentModal({
  open,
  submissionName = "",
  logistics = null,
  onLater,
  onNext,
  onCancel,
}) {
  const [step, setStep] = useState(STEPS.CHOICE);
  const [createPickup, setCreatePickup] = useState(null);
  const [pickupMethod, setPickupMethod] = useState(null);
  const wantsPickup = createPickup === "yes";
  const showMethods = wantsPickup && step === STEPS.PICKUP_METHOD;

  const visibleMethods = useMemo(() => {
    const allowedKeys = getAllowedPickupKeys(logistics);
    return PICKUP_METHODS.filter((method) => allowedKeys.has(method.key));
  }, [logistics]);

  const resetState = () => {
    setStep(STEPS.CHOICE);
    setCreatePickup(null);
    setPickupMethod(null);
  };

  const handleClose = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (onLater) onLater();
  };

  const handleSelectYes = () => {
    setCreatePickup("yes");
  };

  const handleSelectNo = () => {
    setCreatePickup("no");
    setPickupMethod(null);
    setStep(STEPS.CHOICE);
  };

  const canProceed = showMethods
    ? Boolean(pickupMethod)
    : createPickup !== null;

  const handleNext = () => {
    if (!canProceed) return;
    if (wantsPickup && step === STEPS.CHOICE) {
      setStep(STEPS.PICKUP_METHOD);
      return;
    }
    if (onNext) {
      onNext({
        createPickup: wantsPickup,
        pickupMethod: wantsPickup ? pickupMethod : null,
      });
    }
  };

  const renderTitle = (
    <div className="ScheduleShipmentModal__TitleWrap">
      <div className="ScheduleShipmentModal__TitleBlock" />
      <div className="ScheduleShipmentModal__TitleText">
        <span className="ScheduleShipmentModal__Title">Schedule Shipment</span>
        {submissionName ? (
          <span className="ScheduleShipmentModal__Subtitle">{submissionName}</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <Modal
      centered
      open={open}
      title={renderTitle}
      onCancel={handleClose}
      footer={null}
      width={showMethods ? 640 : 520}
      destroyOnClose
      maskClosable={false}
      zIndex={1100}
      getContainer={document.body}
      closeIcon={<CloseOutlined className="ScheduleShipmentModal__CloseIcon" />}
      className={`ScheduleShipmentModal${showMethods ? " ScheduleShipmentModal--expanded" : ""}`}
      afterClose={resetState}
    >
      <div className="ScheduleShipmentModal__Body">
        <p className="ScheduleShipmentModal__Prompt">
          Would you like create pickup request for this Submission?
        </p>
        <Radio.Group
          className="ScheduleShipmentModal__Choices"
          name="schedule-shipment-pickup"
          value={createPickup}
          aria-label="Create pickup request"
        >
          <div
            className={`ScheduleShipmentModal__RadioOption${createPickup === "yes" ? " ScheduleShipmentModal__RadioOption--checked" : ""}`}
            onClick={handleSelectYes}
          >
            <Radio value="yes">Yes</Radio>
          </div>
          <div
            className={`ScheduleShipmentModal__RadioOption${createPickup === "no" ? " ScheduleShipmentModal__RadioOption--checked" : ""}`}
            onClick={handleSelectNo}
          >
            <Radio value="no">No</Radio>
          </div>
        </Radio.Group>

        {showMethods && (
          <Radio.Group
            className="ScheduleShipmentModal__Methods"
            name="schedule-shipment-method"
            value={pickupMethod}
            onChange={(e) => setPickupMethod(e.target.value)}
          >
            {visibleMethods.map((method) => {
              const { Icon } = method;
              return (
                <div
                  key={method.key}
                  className={`ScheduleShipmentModal__MethodCard${pickupMethod === method.key ? " ScheduleShipmentModal__MethodCard--active" : ""}`}
                  onClick={() => setPickupMethod(method.key)}
                >
                  <Radio value={method.key} />
                  <span className="ScheduleShipmentModal__MethodIcon" aria-hidden>
                    <Icon />
                  </span>
                  <span className="ScheduleShipmentModal__MethodText">
                    <span className="ScheduleShipmentModal__MethodTitle">{method.title}</span>
                    <span className="ScheduleShipmentModal__MethodDescription">{method.description}</span>
                  </span>
                  <RightOutlined className="ScheduleShipmentModal__MethodChevron" aria-hidden />
                </div>
              );
            })}
          </Radio.Group>
        )}
      </div>
      <div className="ScheduleShipmentModal__FooterDivider" role="separator" aria-hidden />
      <div className="ScheduleShipmentModal__Footer">
        <Button type="default" onClick={onLater || handleClose}>
          Later
        </Button>
        <Button
          type="primary"
          disabled={!canProceed}
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </Modal>
  );
}
